import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

export interface SymbolSearchResult {
  symbol: string;
  name: string;
  exchange: string;
  tradable: boolean;
}

export interface StockInfo {
  symbol: string;
  currentPrice: number;
  volume: number;
  timestamp: string;
  high: number;
  low: number;
  open: number;
}

// Cache for recent searches
const searchCache = new Map<string, { results: SymbolSearchResult[], timestamp: number }>();
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes (shorter than backend cache)

export class StockLookupService {
  /**
   * Search for stock symbols by query
   * @param query - Partial symbol or company name
   * @returns Array of matching stocks
   */
  static async searchSymbols(query: string): Promise<SymbolSearchResult[]> {
    if (!query || query.trim().length < 1) {
      return [];
    }

    const trimmedQuery = query.trim().toUpperCase();

    // Check local cache first
    const cached = searchCache.get(trimmedQuery);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.results;
    }

    try {
      const lookupStockSymbol = httpsCallable(functions, 'lookupStockSymbol');
      const result = await lookupStockSymbol({ query: trimmedQuery });
      const response = result.data as { success: boolean; data: SymbolSearchResult[] };

      if (response.success && response.data) {
        // Cache the results
        searchCache.set(trimmedQuery, { 
          results: response.data, 
          timestamp: Date.now() 
        });

        // Clean up old cache entries
        if (searchCache.size > 50) {
          const entriesToDelete: string[] = [];
          searchCache.forEach((value, key) => {
            if (Date.now() - value.timestamp > CACHE_TTL) {
              entriesToDelete.push(key);
            }
          });
          entriesToDelete.forEach(key => searchCache.delete(key));
        }

        return response.data;
      }

      return [];
    } catch (error) {
      console.error('Error searching symbols:', error);
      throw error;
    }
  }

  /**
   * Get detailed stock info including price and volume
   * @param symbol - Stock symbol
   * @returns Stock info with current price and volume
   */
  static async getStockInfo(symbol: string): Promise<StockInfo> {
    if (!symbol || symbol.trim().length === 0) {
      throw new Error('Symbol is required');
    }

    try {
      const getStockInfo = httpsCallable(functions, 'getStockInfo');
      const result = await getStockInfo({ symbol: symbol.trim().toUpperCase() });
      const response = result.data as { success: boolean; data: StockInfo };

      if (response.success && response.data) {
        return response.data;
      }

      throw new Error('Failed to get stock info');
    } catch (error) {
      console.error('Error getting stock info:', error);
      throw error;
    }
  }

  /**
   * Clear the search cache
   */
  static clearCache(): void {
    searchCache.clear();
  }
}